"use client";

import { persianToEnglish } from "@/utils/utils";
import React, { useState } from "react";
import { FieldError } from "react-hook-form";

interface TextFieldProps {
  label?: string;
  type?: string;
  placeholder?: string;
  value?: string | number | null;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  ref?: React.Ref<HTMLInputElement>;
  wrapperClassName?: string;
  inputClassName?: string;
  maxLength?: number;
  minLength?: number;
  disabled?: boolean;
  required?: boolean;
  formatAsNumber?: boolean;
  thousandSeparator?: string;
  allowDecimal?: boolean;
  errors?: FieldError;
}

const formatNumberValue = (
  val: string | number | null | undefined,
  allowDecimal: boolean,
  thousandSeparator: string,
): string => {
  if (val == null || val === "") return "";

  // Convert Persian digits first
  let str = persianToEnglish(String(val));

  // Keep only valid number characters
  const regex = allowDecimal ? /[^0-9.]/g : /\D/g;
  str = str.replace(regex, "");

  if (!str) return "";

  // Add thousand separators (only integer part)
  const [integer, decimal = ""] = str.split(".");
  const formattedInteger = Number(integer)
    .toLocaleString("en-US", {
      useGrouping: true,
    })
    .replaceAll(",", thousandSeparator);

  return decimal ? `${formattedInteger}.${decimal}` : formattedInteger;
};

const toDisplayValue = (
  value: string | number | null | undefined,
  formatAsNumber: boolean,
  allowDecimal: boolean,
  thousandSeparator: string,
): string => {
  if (formatAsNumber) {
    return formatNumberValue(value, allowDecimal, thousandSeparator);
  }
  return value === null || value === undefined ? "" : persianToEnglish(String(value));
};

const TextField: React.FC<TextFieldProps> = ({
  label,
  type = "text",
  value,
  onChange,
  onBlur,
  placeholder,
  ref,
  wrapperClassName,
  inputClassName,
  maxLength,
  minLength,
  disabled = false,
  required = false,
  formatAsNumber = false,
  thousandSeparator = ",",
  allowDecimal = false,
  errors,
}) => {
  const [displayValue, setDisplayValue] = useState(() =>
    toDisplayValue(value, formatAsNumber, allowDecimal, thousandSeparator),
  );
  const [prevValue, setPrevValue] = useState(value);
  const [prevFormatAsNumber, setPrevFormatAsNumber] = useState(formatAsNumber);

  // Sync display when external value / format mode changes (during render)
  if (value !== prevValue || formatAsNumber !== prevFormatAsNumber) {
    setPrevValue(value);
    setPrevFormatAsNumber(formatAsNumber);
    setDisplayValue(
      toDisplayValue(value, formatAsNumber, allowDecimal, thousandSeparator),
    );
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    // convert Persian digits to English
    const processed = persianToEnglish(input);

    let finalDisplay = processed;
    let finalValueForForm = processed;

    if (formatAsNumber) {
      // Restrict to numbers only
      const regex = allowDecimal ? /[^0-9.]/g : /\D/g;
      finalDisplay = processed.replace(regex, "");

      // We send clean number string to form (without " , ")
      finalValueForForm = finalDisplay;
    }

    setDisplayValue(finalDisplay);

    // Create synthetic event with clean value for onChange
    const syntheticEvent = {
      ...e,
      target: { ...e.target, value: finalValueForForm },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange?.(syntheticEvent);
  };

  return (
    <div className={`flex flex-col gap-1 w-full ${wrapperClassName || ""}`}>
      {label && (
        <label className="text-sm font-medium text-neutral-600">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <input
        type={formatAsNumber ? "text" : type} // use "text" for formatted numbers too
        value={displayValue}
        onChange={handleChange}
        onBlur={(e) => {
          onBlur?.(e);
        }}
        ref={ref}
        placeholder={placeholder}
        maxLength={maxLength}
        minLength={minLength}
        disabled={disabled}
        required={required}
        className={`
          w-full px-4 py-2.5 rounded-lg 
          bg-[#F2F4F7] 
          text-[#1E1D39] 
          placeholder:text-neutral-400 
          border border-neutral-300
          focus:outline-none ${errors ? "border-red-500 focus:none" : "focus:ring-1 focus:ring-[#444CE7]"}
          transition-all duration-200
          disabled:opacity-60 disabled:cursor-not-allowed
          ${inputClassName || ""}
        `}
      />
    </div>
  );
};

export default TextField;
